import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { FILTROS_CLICAVEIS, FILTROS_DIGITAVEIS, alternarFiltro, descreverFiltro, digitarFiltro, filtroAtivo, valorDigitado } from '../utils/filtrosColunas';

const HEADER_BLUE = '#163455';
const DURACAO_ANIMACAO = '220ms';

// O chip de um botão clicável usa o texto do botão ("Sem carreta"); os digitados, "Placa cavalo: contém …".
const ROTULO_CLICAVEL = Object.fromEntries(FILTROS_CLICAVEIS.flatMap(grupo => grupo.itens.map(item => [item.filtro.id, item.rotulo])));
const rotuloDoChip = filtro => ROTULO_CLICAVEL[filtro.id] || descreverFiltro(filtro);

/**
 * Barra de filtros da programação (vale para os dois turnos). Os filtros ativos aparecem como chips; o botão
 * "Filtros" expande o próprio container (com animação) em duas partes separadas:
 * - "Clique para filtrar": botões que ligam/desligam um critério na hora (pendências, situação, marcações);
 * - "Digite para filtrar": caixas para buscar um valor específico, aplicadas enquanto se digita.
 * O atalho das colunas fica no mesmo lugar.
 */
const ProgramacaoHavanFiltrosBar = ({ filtros, onChangeFiltros, onAbrirColunas, colunasPersonalizadas }) => {
  const [aberto, setAberto] = useState(false);

  return (
    <Barra aria-label="Filtros da programação" $aberto={aberto}>
      <Topo>
        <Lado>
          <BotaoFiltros type="button" aria-expanded={aberto} aria-controls="painel-filtros-havan" $aberto={aberto} onClick={() => setAberto(atual => !atual)}>
            <Seta $aberto={aberto} aria-hidden="true">▾</Seta> Filtros{filtros.length ? ` (${filtros.length})` : ''}
          </BotaoFiltros>
          {filtros.map(filtro => (
            <Chip key={filtro.id}>
              <span>{rotuloDoChip(filtro)}</span>
              <RemoverChip type="button" aria-label={`Remover filtro ${rotuloDoChip(filtro)}`} onClick={() => onChangeFiltros(filtros.filter(atual => atual.id !== filtro.id))}>×</RemoverChip>
            </Chip>
          ))}
          {filtros.length ? <Limpar type="button" onClick={() => onChangeFiltros([])}>Limpar filtros</Limpar> : null}
        </Lado>
        <BotaoBarra type="button" onClick={onAbrirColunas} title="Ordem, visibilidade e largura das colunas (vale para os dois turnos)">
          Colunas{colunasPersonalizadas ? ' · personalizado' : ''}
        </BotaoBarra>
      </Topo>

      {/* Fica montado para animar a abertura e o fechamento; fechado, sai da leitura e do Tab (aria-hidden + inert). */}
      <Recolhivel $aberto={aberto} aria-hidden={!aberto} inert={!aberto}>
        <Conteudo>
          <Painel id="painel-filtros-havan" onKeyDown={event => { if (event.key === 'Escape') setAberto(false); }}>
            <Parte aria-label="Clique para filtrar">
              <TituloParte>Clique para filtrar</TituloParte>
              {FILTROS_CLICAVEIS.map(({ grupo, itens }) => (
                <LinhaGrupo key={grupo}>
                  <RotuloGrupo>{grupo}</RotuloGrupo>
                  <Botoes role="group" aria-label={grupo}>
                    {itens.map(({ rotulo, filtro }) => {
                      const ligado = filtroAtivo(filtros, filtro);
                      return (
                        <BotaoCriterio key={filtro.id} type="button" aria-pressed={ligado} $ligado={ligado} onClick={() => onChangeFiltros(alternarFiltro(filtros, filtro))}>
                          {ligado ? '✓ ' : ''}{rotulo}
                        </BotaoCriterio>
                      );
                    })}
                  </Botoes>
                </LinhaGrupo>
              ))}
            </Parte>

            <Parte aria-label="Digite para filtrar">
              <TituloParte>Digite para filtrar</TituloParte>
              {FILTROS_DIGITAVEIS.map(({ grupo, campos }) => (
                <LinhaGrupo key={grupo}>
                  <RotuloGrupo>{grupo}</RotuloGrupo>
                  <Grade>
                    {campos.map(campo => (
                      <Campo key={campo.id}>
                        <Form.Label htmlFor={`filtro-havan-${campo.id}`}>{campo.rotulo}</Form.Label>
                        <Form.Control
                          id={`filtro-havan-${campo.id}`}
                          size="sm"
                          type={campo.tipo === 'data' ? 'date' : 'search'}
                          placeholder={campo.tipo === 'data' ? undefined : 'Digite…'}
                          value={valorDigitado(filtros, campo.id)}
                          onChange={event => onChangeFiltros(digitarFiltro(filtros, campo, event.target.value))}
                        />
                      </Campo>
                    ))}
                  </Grade>
                </LinhaGrupo>
              ))}
            </Parte>

            <Rodape>
              <Dica>Os filtros valem para os dois turnos e se somam (a carga precisa atender a todos).</Dica>
              <BotaoSecundario type="button" onClick={() => setAberto(false)}>Fechar</BotaoSecundario>
            </Rodape>
          </Painel>
        </Conteudo>
      </Recolhivel>
    </Barra>
  );
};

ProgramacaoHavanFiltrosBar.propTypes = {
  filtros: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, coluna: PropTypes.string, condicao: PropTypes.string, valor: PropTypes.string })),
  onChangeFiltros: PropTypes.func.isRequired,
  onAbrirColunas: PropTypes.func.isRequired,
  colunasPersonalizadas: PropTypes.bool
};

ProgramacaoHavanFiltrosBar.defaultProps = {
  filtros: [],
  colunasPersonalizadas: false
};

export default ProgramacaoHavanFiltrosBar;

const Barra = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  border: 1px solid ${({ $aberto }) => ($aberto ? '#9ec3e6' : '#d7e0ea')};
  border-radius: 4px;
  background: #f8fafc;
  transition: border-color ${DURACAO_ANIMACAO} ease;
`;
const Topo = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;`;
const Lado = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;
// Altura animada sem medir o conteúdo: a linha do grid vai de 0fr a 1fr.
const Recolhivel = styled.div`
  display: grid;
  grid-template-rows: ${({ $aberto }) => ($aberto ? '1fr' : '0fr')};
  opacity: ${({ $aberto }) => ($aberto ? 1 : 0)};
  visibility: ${({ $aberto }) => ($aberto ? 'visible' : 'hidden')};
  transition: grid-template-rows ${DURACAO_ANIMACAO} ease, opacity ${DURACAO_ANIMACAO} ease,
    visibility 0s linear ${({ $aberto }) => ($aberto ? '0s' : DURACAO_ANIMACAO)};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const Conteudo = styled.div`min-height: 0; overflow: hidden;`;
const Painel = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #d7e0ea;
  font-size: 13px;
`;
const Parte = styled.section`
  margin-bottom: 12px;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #ffffff;
`;
const TituloParte = styled.h6`margin: 0 0 8px; color: ${HEADER_BLUE}; font-size: 12px; font-weight: 700; text-transform: uppercase;`;
const LinhaGrupo = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: start;
  gap: 8px;
  & + & { margin-top: 8px; }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;
const RotuloGrupo = styled.span`padding-top: 4px; color: #526275; font-size: 12px; font-weight: 600;`;
const Botoes = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const BotaoCriterio = styled.button`
  padding: 3px 10px;
  border: 1px solid ${({ $ligado }) => ($ligado ? HEADER_BLUE : '#cbd5e1')};
  border-radius: 999px;
  background: ${({ $ligado }) => ($ligado ? HEADER_BLUE : '#ffffff')};
  color: ${({ $ligado }) => ($ligado ? '#ffffff' : '#334155')};
  font-size: 12px;
  font-weight: 600;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
  &:hover { border-color: ${HEADER_BLUE}; }
  &:focus-visible { outline: 2px solid #9ec3e6; outline-offset: 1px; }
`;
const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px 12px;
`;
const Campo = styled.div`
  min-width: 0;
  label { margin-bottom: 2px; color: #526275; font-size: 12px; font-weight: 600; }
`;
const Rodape = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;`;
const Dica = styled.span`color: #526275; font-size: 12px;`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px 2px 10px;
  border: 1px solid #9ec3e6;
  border-radius: 999px;
  background: #eef5fc;
  color: ${HEADER_BLUE};
  font-size: 12px;
  font-weight: 600;
`;
const RemoverChip = styled.button`
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${HEADER_BLUE};
  font-size: 14px;
  line-height: 1;
  &:hover { background: #d6e6f6; }
`;
const BotaoBarra = styled.button`
  padding: 3px 10px;
  border: 1px solid ${HEADER_BLUE};
  border-radius: 4px;
  background: #ffffff;
  color: ${HEADER_BLUE};
  font-size: 12px;
  font-weight: 600;
  &:hover { background: #eef3f8; }
`;
const BotaoFiltros = styled(BotaoBarra)`
  background: ${({ $aberto }) => ($aberto ? HEADER_BLUE : '#ffffff')};
  color: ${({ $aberto }) => ($aberto ? '#ffffff' : HEADER_BLUE)};
  transition: background-color ${DURACAO_ANIMACAO} ease, color ${DURACAO_ANIMACAO} ease;
  &:hover { background: ${({ $aberto }) => ($aberto ? HEADER_BLUE : '#eef3f8')}; }
`;
const Seta = styled.span`
  display: inline-block;
  transform: rotate(${({ $aberto }) => ($aberto ? '180deg' : '0deg')});
  transition: transform ${DURACAO_ANIMACAO} ease;
`;
const Limpar = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: #a61e2a;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
`;
const BotaoSecundario = styled.button`
  padding: 4px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
`;
